import React from 'react';
import { focusRing } from '@shared/utils/statusTone';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  isLoading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  children,
  isLoading,
  className = '',
  disabled,
  ...props
}) => {
  const baseStyles = `inline-flex items-center justify-center gap-2 font-semibold transition-colors rounded-lg disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-100 disabled:shadow-none disabled:bg-surface-2-light disabled:text-text-muted-light dark:disabled:bg-surface-2-dark dark:disabled:text-text-muted-dark ${focusRing}`;

  // Contrast contract (see shared/utils/statusTone.ts): white on brand-600 = 6.29,
  // on-accent-dark on brand-400 = 8.27. `outline` uses border-strong, which only
  // reaches 3:1 over a white `surface-card` — do not place it on `surface-light`.
  const variants = {
    primary:
      'bg-brand-600 text-on-accent-light shadow-lg shadow-brand-200 dark:shadow-none dark:bg-brand-400 dark:text-on-accent-dark dark:hover:bg-brand-300 hover:bg-brand-700',
    secondary:
      'bg-surface-2-light dark:bg-surface-2-dark text-slate-900 dark:text-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700 border border-surface-border-light dark:border-surface-border-dark',
    outline:
      'bg-transparent border-2 border-border-strong-light dark:border-border-strong-dark text-slate-700 dark:text-slate-200 hover:border-brand-600 hover:text-brand-600 dark:hover:border-brand-400 dark:hover:text-brand-400',
    ghost:
      'bg-transparent text-text-muted-light dark:text-text-muted-dark hover:bg-surface-2-light dark:hover:bg-surface-2-dark hover:text-slate-900 dark:hover:text-white',
    danger:
      'bg-state-danger-light text-white shadow-lg shadow-state-danger-light/20 dark:shadow-none dark:bg-state-danger-dark dark:text-on-accent-dark hover:opacity-90',
  };

  const sizes = {
    sm: 'min-h-11 px-3.5 py-2 text-xs',
    md: 'min-h-11 px-4 py-2.5 text-sm',
    lg: 'min-h-12 px-5 py-3 text-base',
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <svg
          className="animate-spin -ml-1 mr-3 h-4 w-4 text-current"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      )}
      {children}
    </button>
  );
};
